import { useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { useApp } from "../../../store.jsx";
import { Icon, StatCard, Avatar } from "../../../components/Common.jsx";
import { fmt, greeting } from "../../../data.js";
import { useReviewsUnread } from "../../../hooks/useReviewsUnread";
import AnnouncementBanner from "../../../components/announcements/AnnouncementBanner.jsx";
import { ReportModal, SubmitReportModal } from "../../../components/Modals.jsx";
import {
  getReportDraft,
  downloadReportsExport,
  listReports,
  api,
} from "../../../lib/api";
import { useSession } from "../../../lib/auth-client";

const cssVar = (n) =>
  getComputedStyle(document.documentElement).getPropertyValue(n).trim();

const todayStr = () => new Date().toISOString().slice(0, 10);
const dayStr = (d) => new Date(d).toISOString().slice(0, 10);

const computeStreak = (reports) => {
  if (!reports.length) return 0;
  const days = new Set(reports.map((r) => dayStr(r.date)));
  let s = 0;
  const d = new Date();
  while (true) {
    const ds = dayStr(d);
    if (days.has(ds)) {
      s++;
      d.setDate(d.getDate() - 1);
    } else break;
  }
  return s;
};

const parseBlockers = (b) => {
  if (!b) return [];
  if (Array.isArray(b)) return b;
  try {
    const v = JSON.parse(b);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export default function EmployeeDashboard() {
  const { toast, openModal, closeModal, goPage } = useApp();
  const { data: session } = useSession();
  const sessionUser = session?.user;
  const { count: newReviews } = useReviewsUnread(true);

  // ─── Server data ────────────────────────────────────────────────────────
  const [me, setMe] = useState(null); // GET /api/me result (with manager)
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [draft, setDraft] = useState({ items: [], loading: true });

  const refresh = async () => {
    try {
      const [meRes, repRes] = await Promise.all([
        api.get("/api/me"),
        listReports(),
      ]);
      setMe(meRes?.user || null);
      setReports(repRes?.reports || []);
    } catch (e) {
      toast(e.message || "Could not load dashboard", "error");
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    getReportDraft()
      .then(({ items = [] }) => setDraft({ items, loading: false }))
      .catch(() => setDraft({ items: [], loading: false }));
  }, []); // eslint-disable-line

  // ─── Derived metrics ────────────────────────────────────────────────────
  const today = todayStr();
  const submittedToday = useMemo(
    () => reports.some((r) => dayStr(r.date) === today),
    [reports, today],
  );

  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const thisMonth = useMemo(
    () => reports.filter((r) => new Date(r.date) >= monthStart).length,
    [reports, monthStart],
  );

  const streak = useMemo(() => computeStreak(reports), [reports]);

  const compliance = useMemo(() => {
    // % of last 30 days where I submitted a report.
    const days = new Set(reports.map((r) => dayStr(r.date)));
    let hit = 0;
    for (let i = 0; i < 30; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      if (days.has(dayStr(d))) hit++;
    }
    return Math.round((hit / 30) * 100);
  }, [reports]);

  const taskBreakdown = useMemo(() => {
    // Flatten all reportTasks from the last 30 days, count by status.
    const since = new Date();
    since.setDate(since.getDate() - 30);
    const counts = { done: 0, in_process: 0, pending: 0 };
    for (const r of reports) {
      if (new Date(r.date) < since) continue;
      for (const rt of r.reportTasks || []) {
        if (counts[rt.status] != null) counts[rt.status]++;
      }
    }
    return counts;
  }, [reports]);

  const draftBreakdown = useMemo(
    () =>
      draft.items.reduce(
        (a, it) => {
          const k = it.suggestedStatus || "new";
          a[k] = (a[k] || 0) + 1;
          return a;
        },
        { new: 0, in_process: 0, pending: 0, done: 0 },
      ),
    [draft.items],
  );

  // ─── Charts ─────────────────────────────────────────────────────────────
  const trendRef = useRef(null);
  const trendChart = useRef(null);
  const statRef = useRef(null);
  const statChart = useRef(null);

  useEffect(() => {
    if (!trendRef.current) return;
    if (trendChart.current) trendChart.current.destroy();

    const days = new Set(reports.map((r) => dayStr(r.date)));
    const labels = [];
    const data = [];
    for (let d = 13; d >= 0; d--) {
      const day = new Date();
      day.setDate(day.getDate() - d);
      labels.push(
        day.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
      );
      data.push(days.has(dayStr(day)) ? 1 : 0);
    }
    trendChart.current = new Chart(trendRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Submitted",
            data,
            backgroundColor: data.map((v) =>
              v ? "#8B5CF6" : "rgba(148,163,184,.25)",
            ),
            borderRadius: 4,
            barThickness: 14,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => (ctx.parsed.y ? "Submitted" : "Missed"),
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: cssVar("--tick-color") || "#6B7280",
              font: { size: 10 },
            },
          },
          y: { display: false, beginAtZero: true, max: 1 },
        },
      },
    });
    return () => trendChart.current?.destroy();
  }, [reports]);

  useEffect(() => {
    if (!statRef.current) return;
    if (statChart.current) statChart.current.destroy();

    const { done, in_process, pending } = taskBreakdown;
    const empty = done + in_process + pending === 0;

    statChart.current = new Chart(statRef.current, {
      type: "doughnut",
      data: {
        labels: ["Done", "In Process", "Pending"],
        datasets: [
          {
            data: empty ? [1] : [done, in_process, pending],
            backgroundColor: empty
              ? ["rgba(148,163,184,.25)"]
              : ["#10B981", "#F59E0B", "#EF4444"],
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        layout: { padding: 6 },
        plugins: {
          legend: empty
            ? { display: false }
            : {
                position: "bottom",
                labels: {
                  color: cssVar("--text-muted") || "#94A3B8",
                  padding: 10,
                  font: { size: 11 },
                  boxWidth: 10,
                  boxHeight: 10,
                  usePointStyle: true,
                  pointStyle: "circle",
                },
              },
          tooltip: {
            enabled: !empty,
            callbacks: {
              label: (ctx) => `${ctx.label}: ${ctx.parsed}`,
            },
          },
        },
      },
    });
    return () => statChart.current?.destroy();
  }, [taskBreakdown]);

  // ─── Recent reports ────────────────────────────────────────────────────
  const myFeed = useMemo(
    () =>
      [...reports]
        .sort(
          (a, b) =>
            new Date(b.createdAt || b.date).getTime() -
            new Date(a.createdAt || a.date).getTime(),
        )
        .slice(0, 6),
    [reports],
  );

  const viewReport = (r) =>
    openModal(
      <ReportModal
        report={{
          ...r,
          employee: r.user || me,
          tasks: (r.reportTasks || []).length
            ? r.reportTasks.map((rt) => ({
                text: rt.task?.title || "(task)",
                status: rt.status,
                note: rt.note,
                source: rt.task?.source,
                carried: !!rt.carriedFrom,
              }))
            : [{ text: r.message || "", status: "done" }],
          blockers: [
            ...parseBlockers(r.analysis?.blockers),
            ...(r.reportTasks || [])
              .filter((rt) => rt.status === "pending" && rt.note)
              .map((rt) => rt.note),
          ],
          aiSummary: r.analysis?.summary || "AI summary pending…",
          sentiment: r.analysis?.sentiment || "neutral",
          source: r.source || "web",
          hoursWorked: 0,
        }}
        onClose={closeModal}
      />,
      "lg",
    );

  const submitReport = () =>
    openModal(
      <SubmitReportModal
        onClose={closeModal}
        onSubmit={async () => {
          toast("Report submitted", "success");
          await refresh();
          getReportDraft()
            .then(({ items = [] }) => setDraft({ items, loading: false }))
            .catch(() => {});
        }}
      />,
      "md",
    );

  const exportMine = async () => {
    try {
      toast("Preparing export…", "info");
      await downloadReportsExport({ scope: "mine" });
      toast("Export downloaded", "success");
    } catch (e) {
      toast(e.message || "Export failed", "error");
    }
  };

  const firstName = (sessionUser?.name || me?.name || "there").split(" ")[0];
  const manager = me?.manager;

  return (
    <section>
      <AnnouncementBanner placement="dashboard" />
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting()}, {firstName} 👋</h1>
          <p className="page-sub">
            Your personal report overview for {fmt.date(new Date())}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn btn-ghost btn-sm" onClick={exportMine}>
            <Icon name="download" size={16} /> Export my reports
          </button>
          <button className="btn btn-primary btn-sm" onClick={submitReport}>
            <Icon name="send" size={16} />{" "}
            {submittedToday ? "Submit another report" : "Submit today's report"}
          </button>
        </div>
      </div>

      {!reportsLoading && !submittedToday && (
        <div
          className="card"
          style={{
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(124,58,237,0.06))",
            borderColor: "rgba(139,92,246,0.4)",
            marginBottom: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(139,92,246,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="message-circle" size={22} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                You haven't submitted today's report
              </div>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                Reply to the WhatsApp bot or submit here directly.
              </div>
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={submitReport}>
            Submit now <Icon name="arrow-right" size={14} />
          </button>
        </div>
      )}

      {newReviews > 0 && (
        <div
          className="card"
          style={{
            background: "linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.05))",
            borderColor: "rgba(245,158,11,0.4)",
            marginBottom: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(245,158,11,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="award" size={22} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                You have {newReviews} new performance review{newReviews === 1 ? "" : "s"}
              </div>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                Your manager published feedback for you.
              </div>
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => goPage("performance")}>
            View <Icon name="arrow-right" size={14} />
          </button>
        </div>
      )}

      <div className="stats-row">
        <StatCard
          label="Reports This Month"
          value={fmt.num(thisMonth)}
          delta={thisMonth > 0 ? `${thisMonth} submitted` : "none yet"}
          dir="up"
          icon="file-text"
          color="primary"
        />
        <StatCard
          label="Current Streak"
          value={streak + (streak === 1 ? " day" : " days")}
          delta={streak >= 5 ? "on fire" : streak > 0 ? "keep going" : "—"}
          dir="up"
          icon="flame"
          color="amber"
        />
        <StatCard
          label="My Compliance"
          value={compliance + "%"}
          delta="last 30 days"
          dir="up"
          icon="shield-check"
          color="green"
        />
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div className="card-title">Your tasks today</div>
            <div className="card-subtitle">
              {draft.loading
                ? "Loading…"
                : draft.items.length === 0
                  ? "No active tasks yet — ask your manager or add your own."
                  : `${draft.items.length} task${draft.items.length === 1 ? "" : "s"} ready for today's report`}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {draftBreakdown.new > 0 && (
              <span className="badge">New · {draftBreakdown.new}</span>
            )}
            {draftBreakdown.in_process > 0 && (
              <span className="badge badge-muted">
                In Process ↻ · {draftBreakdown.in_process}
              </span>
            )}
            {draftBreakdown.pending > 0 && (
              <span className="badge badge-error">
                Pending ↻ · {draftBreakdown.pending}
              </span>
            )}
            {draft.items.length > 0 && (
              <button
                className="btn btn-primary btn-sm"
                onClick={submitReport}
                style={{ marginLeft: 4 }}
              >
                <Icon name="send" size={14} /> Submit
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">My submission trend</div>
              <div className="card-subtitle">Last 14 days</div>
            </div>
          </div>
          <div className="chart-wrap">
            <canvas ref={trendRef} />
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">My task status</div>
              <div className="card-subtitle">Last 30 days</div>
            </div>
          </div>
          <div className="chart-wrap">
            <canvas ref={statRef} />
          </div>
          {taskBreakdown.done +
            taskBreakdown.in_process +
            taskBreakdown.pending ===
            0 && (
            <div
              style={{
                textAlign: "center",
                color: "var(--text-muted)",
                fontSize: 13,
                marginTop: -8,
                marginBottom: 12,
              }}
            >
              No task activity in the last 30 days yet.
            </div>
          )}
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">My recent reports</div>
              <div className="card-subtitle">Click to view full details</div>
            </div>
          </div>
          {reportsLoading ? (
            <div
              style={{
                color: "var(--text-muted)",
                fontSize: 13,
                padding: "20px 0",
                textAlign: "center",
              }}
            >
              Loading…
            </div>
          ) : myFeed.length ? (
            myFeed.map((r) => {
              const tasks = r.reportTasks || [];
              const done = tasks.filter((t) => t.status === "done").length;
              return (
                <div key={r.id} className="activity-row">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "rgba(139,92,246,0.18)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="file-text" size={16} />
                  </div>
                  <div className="activity-text">
                    <div
                      className="activity-title"
                      style={{ fontWeight: 600 }}
                    >
                      {fmt.date(new Date(r.date))}
                    </div>
                    <div className="activity-meta">
                      {tasks.length
                        ? `${done}/${tasks.length} tasks done`
                        : "Free-text report"}
                      {" · via "}
                      {r.source === "whatsapp" ? "WhatsApp" : "Web"}
                    </div>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm activity-view"
                    onClick={() => viewReport(r)}
                  >
                    View
                  </button>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <div className="ico">
                <Icon name="file-text" size={28} />
              </div>
              <h3>No reports yet</h3>
              <p>Submit your first daily report to get started.</p>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">My manager</div>
              <div className="card-subtitle">Reach out anytime</div>
            </div>
          </div>
          {manager ? (
            <div className="activity-row" style={{ borderBottom: "none" }}>
              <Avatar
                name={manager.name}
                size="lg"
                seed={(manager.id || "").length}
              />
              <div className="activity-text">
                <div
                  className="activity-title"
                  style={{ fontWeight: 700, fontSize: 15 }}
                >
                  {manager.name}
                </div>
                <div className="activity-meta">
                  {manager.department ? `${manager.department} · ` : ""}
                  {manager.email}
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="ico">
                <Icon name="user" size={28} />
              </div>
              <h3>No manager assigned</h3>
              <p>Ask your admin to set up your reporting line.</p>
            </div>
          )}

          <div
            style={{
              marginTop: 12,
              padding: 14,
              background: "var(--surface-2)",
              borderRadius: 10,
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                marginBottom: 6,
              }}
            >
              Department
            </div>
            <div style={{ fontWeight: 600 }}>{me?.department || "—"}</div>
          </div>
        </div>
      </div>

    </section>
  );
}
